"use client";

import { useState, useEffect } from "react";
import { Card } from "../ui/Card";
import { Button } from "../ui/Button";
import { Input } from "../ui/Input";
import { Select } from "../ui/Select";
import { PriceRangeSlider } from "../ui/PriceRangeSlider";
import { ItemListFilters } from "../../lib/api/products";
import { INSTRUMENT_CATEGORIES, REGIONS } from "../../data/constants/index";
import { Search, Filter, X } from "lucide-react";
import categoriesData from "../../data/categories.json";

interface ItemFiltersProps {
  filters: ItemListFilters;
  onFiltersChange: (filters: ItemListFilters) => void;
  onClearFilters: () => void;
  showFilters?: boolean;
  onToggleFilters?: (show: boolean) => void;
}

export function ItemFilters({
  filters,
  onFiltersChange,
  onClearFilters,
  showFilters = false,
  onToggleFilters,
}: ItemFiltersProps) {
  const [internalShowFilters, setInternalShowFilters] = useState(showFilters);

  const isFiltersOpen = onToggleFilters ? showFilters : internalShowFilters;
  const setFiltersOpen = onToggleFilters
    ? onToggleFilters
    : setInternalShowFilters;

  const handleFilterChange = (
    key: keyof ItemListFilters,
    value: string | number | undefined
  ) => {
    onFiltersChange({
      ...filters,
      [key]: value || undefined,
    });
  };

  const hasActiveFilters = Object.entries(filters).some(([key, value]) => {
    if (key === "status") return false;
    return value !== undefined && value !== "";
  });

  return (
    <div className="mb-6">
      {/* 검색창 + 필터 버튼 */}
      <div className="flex gap-3 mb-4">
        {/* 검색창 */}
        <div className="flex-1 relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#B9A7FF]" />
          <input
            type="text"
            placeholder="브랜드나 모델명으로 검색..."
            value={filters.keyword || ""}
            onChange={e => handleFilterChange("keyword", e.target.value)}
            className="w-full h-12 pl-12 pr-4 bg-white border border-[#E2E3E7] rounded-[12px] text-[15px] placeholder:text-[#B5AFCF] focus:outline-none focus:border-[#7A56E8] focus:ring-2 focus:ring-[#7A56E8]/18 transition-all duration-200"
          />
        </div>

        {/* 필터 버튼 */}
        <button
          onClick={() => setFiltersOpen(!isFiltersOpen)}
          className={`h-12 px-4 flex items-center justify-center gap-2 bg-white border border-[#E2E3E7] rounded-[12px] text-[14px] font-medium text-gray-700 transition-all duration-200 ${
            isFiltersOpen
              ? "border-[#7A56E8] bg-[#F5F3FF] text-[#7A56E8]"
              : "hover:border-[#7A56E8]/30 hover:shadow-[0_2px_8px_rgba(0,0,0,0.08)]"
          }`}
          style={{
            boxShadow: isFiltersOpen
              ? "0 1px 3px rgba(0, 0, 0, 0.08)"
              : "none",
          }}
        >
          <Filter className="w-5 h-5" />
          <span>필터</span>
          {hasActiveFilters && (
            <span className="ml-1 bg-[#7A56E8] text-white text-[11px] font-bold rounded-full w-5 h-5 flex items-center justify-center">
              {
                Object.values(filters).filter(
                  v => v !== undefined && v !== ""
                ).length
              }
            </span>
          )}
        </button>
      </div>

      {/* 필터 옵션들 */}
      {isFiltersOpen && (
        <Card className="p-6 mb-4">
          <div className="space-y-6">
            {/* 지역 섹션 */}
            <div>
              <label className="block text-[14px] font-semibold text-gray-900 mb-3">
                지역
              </label>
              <select
                className="w-full h-12 px-4 border border-[#D9DCE5] rounded-[12px] text-[15px] focus:outline-none focus:border-[#7A56E8] focus:ring-2 focus:ring-[#7A56E8]/18 transition-all duration-200"
                value={filters.region || ""}
                onChange={e => handleFilterChange("region", e.target.value)}
              >
                <option value="">전체</option>
                {REGIONS.map(region => (
                  <option key={region} value={region}>
                    {region}
                  </option>
                ))}
              </select>
            </div>

            {/* 가격 범위 섹션 */}
            <div>
              <label className="block text-[14px] font-semibold text-gray-900 mb-3">
                가격 범위
              </label>
              <PriceRangeSlider
                minPrice={filters.minPrice}
                maxPrice={filters.maxPrice}
                onPriceChange={(min, max) => {
                  handleFilterChange("minPrice", min);
                  handleFilterChange("maxPrice", max);
                }}
              />
            </div>

            {/* 필터 초기화 버튼 */}
            {hasActiveFilters && (
              <div className="flex justify-end pt-2">
                <button
                  onClick={onClearFilters}
                  className="flex items-center gap-2 px-4 py-2 text-[14px] font-medium text-[#7A56E8] hover:text-[#6D46D1] transition-colors"
                >
                  <X className="w-4 h-4" />
                  필터 초기화
                </button>
              </div>
            )}
          </div>
        </Card>
      )}

      {/* 카테고리 필터 - 칩 형태 */}
      <div className="mb-4">
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => {
              onFiltersChange({
                ...filters,
                category: undefined,
                subcategory: undefined,
                detailCategory: undefined,
              });
            }}
            className={`h-[36px] px-4 rounded-[20px] text-[13px] font-bold transition-all duration-200 ${
              !filters.category
                ? "bg-gradient-to-r from-[#7A56E8] to-[#9B82FF] text-white shadow-[0_2px_8px_rgba(122,86,232,0.25)]"
                : "bg-[#F1F1F3] text-[#7A56E8] hover:bg-[#E8E2FF]"
            }`}
          >
            전체
          </button>

          {INSTRUMENT_CATEGORIES.map(category => (
            <button
              key={category.key}
              onClick={() => {
                onFiltersChange({
                  ...filters,
                  category: category.key,
                  subcategory: undefined,
                  detailCategory: undefined,
                });
              }}
              className={`h-[36px] px-4 rounded-[20px] text-[13px] font-bold transition-all duration-200 ${
                filters.category === category.key
                  ? "bg-gradient-to-r from-[#7A56E8] to-[#9B82FF] text-white shadow-[0_2px_8px_rgba(122,86,232,0.25)]"
                  : "bg-[#F1F1F3] text-[#7A56E8] hover:bg-[#E8E2FF]"
              }`}
            >
              {category.label}
            </button>
          ))}
        </div>
      </div>

      {/* 서브카테고리 (선택된 경우) */}
      {filters.category &&
        (() => {
          const categoryData = categoriesData.categories.find(
            cat =>
              cat.name ===
              INSTRUMENT_CATEGORIES.find(ic => ic.key === filters.category)
                ?.label
          );

          if (!categoryData) return null;

          const subcategories = categoryData.items.map(item =>
            typeof item === "string" ? item : item.name
          );

          return (
            <div className="mb-4">
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => {
                    onFiltersChange({
                      ...filters,
                      subcategory: undefined,
                      detailCategory: undefined,
                    });
                  }}
                  className={`h-[36px] px-4 rounded-[20px] text-[13px] font-bold transition-all duration-200 ${
                    !filters.subcategory
                      ? "bg-gradient-to-r from-[#7A56E8] to-[#9B82FF] text-white shadow-[0_2px_8px_rgba(122,86,232,0.25)]"
                      : "bg-[#F1F1F3] text-[#7A56E8] hover:bg-[#E8E2FF]"
                  }`}
                >
                  전체
                </button>
                {subcategories.map(subcat => (
                  <button
                    key={subcat}
                    onClick={() => {
                      onFiltersChange({
                        ...filters,
                        subcategory: subcat,
                        detailCategory: undefined,
                      });
                    }}
                    className={`h-[36px] px-4 rounded-[20px] text-[13px] font-bold transition-all duration-200 ${
                      filters.subcategory === subcat
                        ? "bg-gradient-to-r from-[#7A56E8] to-[#9B82FF] text-white shadow-[0_2px_8px_rgba(122,86,232,0.25)]"
                        : "bg-[#F1F1F3] text-[#7A56E8] hover:bg-[#E8E2FF]"
                    }`}
                  >
                    {subcat}
                  </button>
                ))}
              </div>
            </div>
          );
        })()}

      {/* 상세 카테고리 (선택된 경우) */}
      {filters.category &&
        filters.subcategory &&
        (() => {
          const categoryData = categoriesData.categories.find(
            cat =>
              cat.name ===
              INSTRUMENT_CATEGORIES.find(ic => ic.key === filters.category)
                ?.label
          );

          if (!categoryData) return null;

          const subcategoryData = categoryData.items.find(
            item =>
              typeof item !== "string" && item.name === filters.subcategory
          );

          if (
            !subcategoryData ||
            typeof subcategoryData === "string" ||
            !subcategoryData.subcategories
          ) {
            return null;
          }

          return (
            <div className="mb-4">
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => {
                    onFiltersChange({
                      ...filters,
                      detailCategory: undefined,
                    });
                  }}
                  className={`h-[36px] px-4 rounded-[20px] text-[13px] font-bold transition-all duration-200 ${
                    !filters.detailCategory
                      ? "bg-gradient-to-r from-[#7A56E8] to-[#9B82FF] text-white shadow-[0_2px_8px_rgba(122,86,232,0.25)]"
                      : "bg-[#F1F1F3] text-[#7A56E8] hover:bg-[#E8E2FF]"
                  }`}
                >
                  전체
                </button>
                {subcategoryData.subcategories.map(detail => (
                  <button
                    key={detail}
                    onClick={() => {
                      onFiltersChange({
                        ...filters,
                        detailCategory: detail,
                      });
                    }}
                    className={`h-[36px] px-4 rounded-[20px] text-[13px] font-bold transition-all duration-200 ${
                      filters.detailCategory === detail
                        ? "bg-gradient-to-r from-[#7A56E8] to-[#9B82FF] text-white shadow-[0_2px_8px_rgba(122,86,232,0.25)]"
                        : "bg-[#F1F1F3] text-[#7A56E8] hover:bg-[#E8E2FF]"
                    }`}
                  >
                    {detail}
                  </button>
                ))}
              </div>
            </div>
          );
        })()}

      {/* 거래 상태 필터 */}
      <div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => handleFilterChange("status", undefined)}
            className={`h-[36px] px-4 rounded-[20px] text-[13px] font-bold transition-all duration-200 ${
              !filters.status
                ? "bg-gradient-to-r from-[#7A56E8] to-[#9B82FF] text-white shadow-[0_2px_8px_rgba(122,86,232,0.25)]"
                : "bg-[#F1F1F3] text-[#7A56E8] hover:bg-[#E8E2FF]"
            }`}
          >
            전체
          </button>
          <button
            onClick={() => handleFilterChange("status", "available")}
            className={`h-[36px] px-4 rounded-[20px] text-[13px] font-bold transition-all duration-200 ${
              filters.status === "available"
                ? "bg-gradient-to-r from-[#7A56E8] to-[#9B82FF] text-white shadow-[0_2px_8px_rgba(122,86,232,0.25)]"
                : "bg-[#F1F1F3] text-[#7A56E8] hover:bg-[#E8E2FF]"
            }`}
          >
            거래가능
          </button>
          <button
            onClick={() => handleFilterChange("status", "reserved")}
            className={`h-[36px] px-4 rounded-[20px] text-[13px] font-bold transition-all duration-200 ${
              filters.status === "reserved"
                ? "bg-gradient-to-r from-[#7A56E8] to-[#9B82FF] text-white shadow-[0_2px_8px_rgba(122,86,232,0.25)]"
                : "bg-[#F1F1F3] text-[#7A56E8] hover:bg-[#E8E2FF]"
            }`}
          >
            거래중
          </button>
          <button
            onClick={() => handleFilterChange("status", "sold")}
            className={`h-[36px] px-4 rounded-[20px] text-[13px] font-bold transition-all duration-200 ${
              filters.status === "sold"
                ? "bg-gradient-to-r from-[#7A56E8] to-[#9B82FF] text-white shadow-[0_2px_8px_rgba(122,86,232,0.25)]"
                : "bg-[#F1F1F3] text-[#7A56E8] hover:bg-[#E8E2FF]"
            }`}
          >
            판매완료
          </button>
        </div>
      </div>
    </div>
  );
}
